/**
 * Graphite theme — CSS variable overrides.
 *
 * A single warm-mid-grey ("warm fog") palette with a terracotta accent and
 * Apple-style materials (translucency, soft shadows, rounded corners, Inter
 * type). There is intentionally NO light/dark fork — the palette sits in the
 * middle so it reads the same in any ambient. The same override map is applied
 * to both the `light` and `dark` slots so the OS / Metronic mode toggle is a
 * no-op while Graphite is active (mirrors how the Terminal theme is force-dark).
 *
 * Companion SCSS lives next to this file in global.scss (scoped to
 * [data-app-theme="graphite"]). Zero tokens are shared with the terminal or
 * Metronic palettes — this object is fully self-contained.
 */

import type { AppThemeDoc, ThemeOverrides } from '../../services/themeCustomizerService';

export type BuiltInTheme = AppThemeDoc & {
  id: string;
  slug: string;
  isBuiltIn: true;
  /** Optional: render under another theme's data-app-theme scope + chrome (a
   *  "family"). Lets a variant (e.g. Blossom) reuse all of graphite's SCSS +
   *  bespoke chrome and only swap the colour tokens. Defaults to `slug`. */
  appThemeAttr?: string;
  /** Optional switcher-swatch override (any CSS `background` value — hex or
   *  gradient). Defaults to the theme's `--bs-primary`. Useful for a dark
   *  variant whose accent alone wouldn't read as "dark" in the picker. */
  swatch?: string;
};

const OVERRIDES: ThemeOverrides = {
  // ── Bootstrap 5 core tokens ────────────────────────────────────────────
  '--bs-body-bg':              '#d2cec7',   // warm fog canvas
  '--bs-body-color':           '#221f1b',   // warm near-black
  '--bs-emphasis-color':       '#15120e',
  '--bs-secondary-color':      '#5e5a53',
  '--bs-tertiary-color':       '#908b82',
  '--bs-secondary-bg':         '#e4e0d9',
  '--bs-tertiary-bg':          '#ece9e4',
  '--bs-body-bg-rgb':          '210, 206, 199',
  '--bs-body-color-rgb':       '34, 31, 27',

  '--bs-border-color':         '#c4bfb6',
  '--bs-border-color-translucent': 'rgba(40, 30, 20, 0.10)',
  // Apple-soft, continuous-feeling corners.
  '--bs-border-radius':        '12px',
  '--bs-border-radius-sm':     '8px',
  '--bs-border-radius-lg':     '16px',
  '--bs-border-radius-xl':     '20px',
  '--bs-border-radius-xxl':    '28px',
  '--bs-border-radius-pill':   '999px',

  // Accent / semantic colours — terracotta accent + warm status tones.
  '--bs-primary':              '#bc5e3a',
  '--bs-primary-rgb':          '188, 94, 58',
  '--bs-primary-bg-subtle':    'rgba(188, 94, 58, 0.12)',
  '--bs-primary-border-subtle':'rgba(188, 94, 58, 0.30)',
  '--bs-primary-text-emphasis':'#9c4a2a',

  '--bs-secondary':            '#8a857c',
  '--bs-secondary-rgb':        '138, 133, 124',

  '--bs-success':              '#4e9d58',
  '--bs-success-rgb':          '78, 157, 88',
  '--bs-success-bg-subtle':    'rgba(78, 157, 88, 0.12)',
  '--bs-success-border-subtle':'rgba(78, 157, 88, 0.30)',

  '--bs-danger':               '#d2462f',
  '--bs-danger-rgb':           '210, 70, 47',
  '--bs-danger-bg-subtle':     'rgba(210, 70, 47, 0.12)',
  '--bs-danger-border-subtle': 'rgba(210, 70, 47, 0.30)',

  '--bs-warning':              '#c2872a',
  '--bs-warning-rgb':          '194, 135, 42',
  '--bs-warning-bg-subtle':    'rgba(194, 135, 42, 0.12)',
  '--bs-warning-border-subtle':'rgba(194, 135, 42, 0.30)',

  '--bs-info':                 '#4a7fa8',
  '--bs-info-rgb':             '74, 127, 168',
  '--bs-info-bg-subtle':       'rgba(74, 127, 168, 0.12)',
  '--bs-info-border-subtle':   'rgba(74, 127, 168, 0.30)',

  // Metronic interactive-state tokens. Metronic's compiled button / badge /
  // bg-light classes consume var(--bs-{color}-active | -light | -inverse), with
  // a baked #009ef7 (#0095e8 active) blue as the ONLY fallback. Graphite set the
  // base --bs-{color} / -rgb but not these, so every hover / active / light
  // state fell back to Metronic blue (e.g. .btn-primary:hover, .bg-light-primary
  // avatars, .badge-light-primary). Define them so all interactive states stay
  // terracotta / warm under Graphite — app-wide, not per-page.
  '--bs-primary-active':       '#a6492a',
  '--bs-primary-light':        'rgba(188, 94, 58, 0.12)',
  '--bs-primary-inverse':      '#ffffff',
  '--bs-success-active':       '#3f8248',
  '--bs-success-light':        'rgba(78, 157, 88, 0.12)',
  '--bs-success-inverse':      '#ffffff',
  '--bs-danger-active':        '#b53a26',
  '--bs-danger-light':         'rgba(210, 70, 47, 0.12)',
  '--bs-danger-inverse':       '#ffffff',
  '--bs-warning-active':       '#a87324',
  '--bs-warning-light':        'rgba(194, 135, 42, 0.12)',
  '--bs-warning-inverse':      '#ffffff',
  '--bs-info-active':          '#3d6a8d',
  '--bs-info-light':           'rgba(74, 127, 168, 0.12)',
  '--bs-info-inverse':         '#ffffff',
  // Neutral families (greys, not blue — these don't leak Metronic blue, but
  // Metronic still consumes their -active/-light/-inverse tokens for .btn-light
  // / .btn-secondary / .btn-dark hovers + text, so define them warm for
  // consistency. -inverse stays DARK on the light family for legible text.
  '--bs-secondary-active':     '#76716a',
  '--bs-secondary-light':      'rgba(138, 133, 124, 0.14)',
  '--bs-secondary-inverse':    '#ffffff',
  '--bs-dark-active':          '#15120e',
  '--bs-dark-light':           'rgba(34, 31, 27, 0.10)',
  '--bs-dark-inverse':         '#ffffff',
  '--bs-light-active':         '#e4e0d9',
  '--bs-light-light':          '#e8e4dd',
  '--bs-light-inverse':        '#221f1b',

  '--bs-light':                '#f3f1ed',
  '--bs-light-rgb':            '243, 241, 237',
  '--bs-dark':                 '#221f1b',
  '--bs-dark-rgb':             '34, 31, 27',

  '--bs-link-color':           '#bc5e3a',
  '--bs-link-color-rgb':       '188, 94, 58',
  '--bs-link-hover-color':     '#9c4a2a',
  '--bs-link-hover-color-rgb': '156, 74, 42',

  // ── Metronic app-level tokens ──────────────────────────────────────────
  '--kt-page-bg':                            '#d2cec7',
  '--kt-app-bg-color':                       '#d2cec7',
  // Translucent header/aside — the SCSS adds the backdrop blur for true glass.
  '--kt-app-header-base-bg-color':           'rgba(236, 233, 228, 0.72)',
  '--kt-app-header-sticky-bg-color':         'rgba(236, 233, 228, 0.82)',
  '--kt-app-header-base-border-color':       '#c8c3ba',
  '--kt-app-sidebar-base-bg-color':          'rgba(236, 233, 228, 0.80)',
  '--kt-app-sidebar-base-border-color':      '#c8c3ba',
  '--kt-app-aside-base-bg-color':            'rgba(236, 233, 228, 0.80)',
  '--kt-app-aside-base-border-color':        '#c8c3ba',
  '--kt-app-footer-bg-color':                '#d2cec7',

  '--kt-card-bg':              '#ece9e4',
  '--kt-card-border-color':    '#d8d3cb',
  '--kt-card-box-shadow':      '0 1px 2px rgba(40, 30, 20, 0.06), 0 8px 24px rgba(40, 30, 20, 0.06)',

  '--kt-text-dark':            '#15120e',
  '--kt-text-gray-900':        '#221f1b',
  '--kt-text-gray-800':        '#3a3631',
  '--kt-text-gray-700':        '#4e4a43',
  '--kt-text-gray-600':        '#5e5a53',
  '--kt-text-gray-500':        '#908b82',
  '--kt-text-gray-400':        '#b8b3aa',
  '--kt-text-gray-300':        '#cfcac2',
  '--kt-text-gray-200':        '#e0dcd4',
  '--kt-text-gray-100':        '#ece9e4',

  '--kt-gray-900':             '#221f1b',
  '--kt-gray-800':             '#3a3631',
  '--kt-gray-700':             '#4e4a43',
  '--kt-gray-600':             '#5e5a53',
  '--kt-gray-500':             '#908b82',
  '--kt-gray-400':             '#b8b3aa',
  '--kt-gray-300':             '#cfcac2',
  '--kt-gray-200':             '#e0dcd4',
  '--kt-gray-100':             '#ece9e4',

  '--kt-primary':              '#bc5e3a',
  '--kt-primary-active':       '#a6492a',
  '--kt-primary-light':        'rgba(188, 94, 58, 0.12)',
  '--kt-primary-inverse':      '#ffffff',

  '--kt-border-color':         '#c4bfb6',
  '--kt-border-dashed-color':  '#cfcac2',

  '--kt-input-bg':             '#f3f1ed',
  '--kt-input-solid-bg':       '#ece9e4',
  '--kt-input-color':          '#221f1b',
  '--kt-input-border-color':   '#c4bfb6',
  '--kt-input-placeholder-color':'#908b82',

  '--kt-menu-link-color-hover':  '#bc5e3a',
  '--kt-menu-link-color-active': '#bc5e3a',
  '--kt-menu-link-bg-hover':     'rgba(188, 94, 58, 0.08)',
  '--kt-menu-link-bg-active':    'rgba(188, 94, 58, 0.12)',

  '--kt-tooltip-bg':           '#2a2722',
  '--kt-tooltip-color':        '#f3f1ed',

  '--kt-modal-bg':             '#ece9e4',
  '--kt-modal-header-border-color': '#d8d3cb',
  '--kt-modal-footer-border-color': '#d8d3cb',

  '--kt-dropdown-bg':          '#f3f1ed',
  '--kt-dropdown-border-color':'#d8d3cb',
  '--kt-dropdown-link-color':  '#221f1b',
  '--kt-dropdown-link-bg-hover':'rgba(188, 94, 58, 0.08)',

  // ── Graphite-specific tokens (consumed by global.scss + the dashboard) ──
  '--graphite-font': "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', system-ui, sans-serif",
  '--graphite-color-scheme': 'light',
  '--graphite-on-accent':  '#ffffff',
  '--graphite-on-positive': '#ffffff',
  '--graphite-canvas':     '#d2cec7',
  '--graphite-surface':    '#ece9e4',
  '--graphite-surface-2':  '#f3f1ed',
  '--graphite-glass':      'rgba(255, 253, 250, 0.55)',
  '--graphite-text':       '#221f1b',
  '--graphite-text-2':     '#5e5a53',
  '--graphite-text-3':     '#908b82',
  '--graphite-sep':        'rgba(40, 30, 20, 0.10)',
  '--graphite-accent':     '#bc5e3a',
  '--graphite-accent-strong': '#b0532f',
  '--graphite-accent-tint':'rgba(188, 94, 58, 0.14)',
  '--graphite-accent-text':'#9c4a2a',
  '--graphite-positive':   '#4e9d58',
  '--graphite-alert':      '#d2462f',
  '--graphite-warn':       '#c2872a',
  '--graphite-shadow-sm':  '0 1px 2px rgba(40, 30, 20, 0.07)',
  '--graphite-shadow':     '0 1px 2px rgba(40, 30, 20, 0.06), 0 8px 24px rgba(40, 30, 20, 0.07)',
  '--graphite-shadow-lg':  '0 12px 40px rgba(40, 30, 20, 0.14)',
};

export const GRAPHITE_THEME: BuiltInTheme = {
  id: '__builtin_graphite',
  slug: 'graphite',
  name: 'Graphite',
  description: 'Apple-inspired warm-fog grey with a terracotta accent. One self-contained palette — no light/dark mode.',
  isBuiltIn: true,
  overrides: {
    light: OVERRIDES,
    dark:  OVERRIDES,
  },
};

// ── Blossom — a pink/floral variant of Graphite ─────────────────────────────
//  Reuses every graphite material (Inter, soft radii, frosted chrome, the wall)
//  by spreading OVERRIDES and re-pointing ONLY the colour tokens: warm-fog beige
//  → soft pink-blush, terracotta → floral rose, warm-brown tints → rose-plum.
//  Semantic status tones (success/danger/warning/info) are kept from graphite.
const BLOSSOM_OVERRIDES: ThemeOverrides = {
  ...OVERRIDES,

  // Surfaces — warm fog → blush.
  '--bs-body-bg':              '#efdde4',
  '--bs-body-color':           '#2b1f26',
  '--bs-emphasis-color':       '#1d141a',
  '--bs-secondary-color':      '#6e5862',
  '--bs-tertiary-color':       '#9d8893',
  '--bs-secondary-bg':         '#f3e3ea',
  '--bs-tertiary-bg':          '#f8ecf0',
  '--bs-body-bg-rgb':          '239, 221, 228',
  '--bs-body-color-rgb':       '43, 31, 38',
  '--bs-border-color':         '#dac6cf',
  '--bs-border-color-translucent': 'rgba(120, 50, 85, 0.10)',

  // Accent — terracotta → floral rose.
  '--bs-primary':              '#c95d87',
  '--bs-primary-rgb':          '201, 93, 135',
  '--bs-primary-bg-subtle':    'rgba(201, 93, 135, 0.12)',
  '--bs-primary-border-subtle':'rgba(201, 93, 135, 0.30)',
  '--bs-primary-text-emphasis':'#a8456f',
  '--bs-primary-active':       '#b34e76',
  '--bs-primary-light':        'rgba(201, 93, 135, 0.12)',
  '--bs-primary-inverse':      '#ffffff',
  '--bs-link-color':           '#c95d87',
  '--bs-link-color-rgb':       '201, 93, 135',
  '--bs-link-hover-color':     '#a8456f',
  '--bs-link-hover-color-rgb': '168, 69, 111',

  // Neutral families — mauve-tinted.
  '--bs-secondary':            '#a48b97',
  '--bs-secondary-rgb':        '164, 139, 151',
  '--bs-secondary-active':     '#917883',
  '--bs-secondary-light':      'rgba(164, 139, 151, 0.14)',
  '--bs-dark':                 '#2b1f26',
  '--bs-dark-rgb':             '43, 31, 38',
  '--bs-dark-active':          '#1d141a',
  '--bs-dark-light':           'rgba(43, 31, 38, 0.10)',
  '--bs-light':                '#faf2f6',
  '--bs-light-rgb':            '250, 242, 246',
  '--bs-light-active':         '#f3e3ea',
  '--bs-light-light':          '#f5e8ee',
  '--bs-light-inverse':        '#2b1f26',

  // Metronic app-level.
  '--kt-page-bg':                            '#efdde4',
  '--kt-app-bg-color':                       '#efdde4',
  '--kt-app-header-base-bg-color':           'rgba(248, 236, 240, 0.72)',
  '--kt-app-header-sticky-bg-color':         'rgba(248, 236, 240, 0.82)',
  '--kt-app-header-base-border-color':       '#e2cfd8',
  '--kt-app-sidebar-base-bg-color':          'rgba(248, 236, 240, 0.80)',
  '--kt-app-sidebar-base-border-color':      '#e2cfd8',
  '--kt-app-aside-base-bg-color':            'rgba(248, 236, 240, 0.80)',
  '--kt-app-aside-base-border-color':        '#e2cfd8',
  '--kt-app-footer-bg-color':                '#efdde4',
  '--kt-card-bg':              '#f8ecf0',
  '--kt-card-border-color':    '#eed9e1',
  '--kt-card-box-shadow':      '0 1px 2px rgba(120, 50, 85, 0.06), 0 8px 24px rgba(120, 50, 85, 0.06)',
  '--kt-text-dark':            '#1d141a',
  '--kt-text-gray-900':        '#2b1f26',
  '--kt-text-gray-800':        '#453742',
  '--kt-text-gray-700':        '#5a4a55',
  '--kt-text-gray-600':        '#6e5862',
  '--kt-text-gray-500':        '#9d8893',
  '--kt-text-gray-400':        '#c2acb6',
  '--kt-text-gray-300':        '#d8c6cf',
  '--kt-text-gray-200':        '#e9d9e0',
  '--kt-text-gray-100':        '#f8ecf0',
  '--kt-gray-900':             '#2b1f26',
  '--kt-gray-800':             '#453742',
  '--kt-gray-700':             '#5a4a55',
  '--kt-gray-600':             '#6e5862',
  '--kt-gray-500':             '#9d8893',
  '--kt-gray-400':             '#c2acb6',
  '--kt-gray-300':             '#d8c6cf',
  '--kt-gray-200':             '#e9d9e0',
  '--kt-gray-100':             '#f8ecf0',
  '--kt-primary':              '#c95d87',
  '--kt-primary-active':       '#b34e76',
  '--kt-primary-light':        'rgba(201, 93, 135, 0.12)',
  '--kt-primary-inverse':      '#ffffff',
  '--kt-border-color':         '#dac6cf',
  '--kt-border-dashed-color':  '#d8c6cf',
  '--kt-input-bg':             '#faf2f6',
  '--kt-input-solid-bg':       '#f8ecf0',
  '--kt-input-color':          '#2b1f26',
  '--kt-input-border-color':   '#dac6cf',
  '--kt-input-placeholder-color':'#9d8893',
  '--kt-menu-link-color-hover':  '#c95d87',
  '--kt-menu-link-color-active': '#c95d87',
  '--kt-menu-link-bg-hover':     'rgba(201, 93, 135, 0.08)',
  '--kt-menu-link-bg-active':    'rgba(201, 93, 135, 0.12)',
  '--kt-tooltip-bg':           '#33232b',
  '--kt-tooltip-color':        '#faf2f6',
  '--kt-modal-bg':             '#f8ecf0',
  '--kt-modal-header-border-color': '#eed9e1',
  '--kt-modal-footer-border-color': '#eed9e1',
  '--kt-dropdown-bg':          '#faf2f6',
  '--kt-dropdown-border-color':'#eed9e1',
  '--kt-dropdown-link-color':  '#2b1f26',
  '--kt-dropdown-link-bg-hover':'rgba(201, 93, 135, 0.08)',

  // Graphite-specific tokens (the wall + bespoke chrome).
  '--graphite-canvas':     '#efdde4',
  '--graphite-surface':    '#f8ecf0',
  '--graphite-surface-2':  '#fcf4f7',
  '--graphite-glass':      'rgba(255, 250, 252, 0.55)',
  '--graphite-text':       '#2b1f26',
  '--graphite-text-2':     '#6e5862',
  '--graphite-text-3':     '#9d8893',
  '--graphite-sep':        'rgba(120, 50, 85, 0.10)',
  '--graphite-accent':     '#c95d87',
  '--graphite-accent-strong': '#bd4f79',
  '--graphite-accent-tint':'rgba(201, 93, 135, 0.14)',
  '--graphite-accent-text':'#a8456f',
  '--graphite-shadow-sm':  '0 1px 2px rgba(120, 50, 85, 0.07)',
  '--graphite-shadow':     '0 1px 2px rgba(120, 50, 85, 0.06), 0 8px 24px rgba(120, 50, 85, 0.07)',
  '--graphite-shadow-lg':  '0 12px 40px rgba(120, 50, 85, 0.14)',
};

export const BLOSSOM_THEME: BuiltInTheme = {
  id: '__builtin_blossom',
  slug: 'blossom',
  name: 'Blossom',
  description: 'Graphite in a soft pink-blush palette with a floral rose accent. One self-contained palette — no light/dark mode.',
  isBuiltIn: true,
  appThemeAttr: 'graphite', // reuse graphite's chrome + scoped SCSS; only the palette differs
  overrides: {
    light: BLOSSOM_OVERRIDES,
    dark:  BLOSSOM_OVERRIDES,
  },
};

// ── Onyx — a NIGHT (dark) variant of Graphite ───────────────────────────────
//  Same graphite family (Inter, soft radii, frosted chrome, the wall) via
//  appThemeAttr:'graphite'. Unlike Blossom (a hue-shift of the light palette),
//  Onyx INVERTS the luminance: warm-charcoal canvas, raised dark surfaces, light
//  text, light-on-dark hairlines/shadows, and the terracotta accent brightened
//  so it glows on dark. The Metronic gray ramp (900→100) is inverted (900 = the
//  brightest text, 100 = a dark surface). A few graphite SCSS values were
//  hardcoded light (chrome glass, aside bg, scrollbar, warn text, hover tints);
//  those now read from --graphite-chrome-glass / --graphite-chrome-aside /
//  --graphite-scroll-* / --graphite-warn-text / --graphite-hover, which Graphite
//  + Blossom leave unset (SCSS fallback = the old light value, so they're
//  unchanged) and Onyx sets dark below.
const ONYX_OVERRIDES: ThemeOverrides = {
  ...OVERRIDES,

  // Surfaces — warm fog → warm charcoal (canvas darkest, surfaces raised lighter).
  '--bs-body-bg':              '#1a1917',
  '--bs-body-color':           '#e9e7e2',
  '--bs-emphasis-color':       '#f5f3ef',
  '--bs-secondary-color':      '#98938b',
  '--bs-tertiary-color':       '#837e76',
  '--bs-secondary-bg':         '#232220',
  '--bs-tertiary-bg':          '#2b2a27',
  '--bs-body-bg-rgb':          '26, 25, 23',
  '--bs-body-color-rgb':       '233, 231, 226',
  '--bs-border-color':         '#3d3b36',
  '--bs-border-color-translucent': 'rgba(255, 249, 242, 0.10)',

  // Accent — terracotta, brightened so it reads on charcoal.
  '--bs-primary':              '#cf6f47',
  '--bs-primary-rgb':          '207, 111, 71',
  '--bs-primary-bg-subtle':    'rgba(207, 111, 71, 0.16)',
  '--bs-primary-border-subtle':'rgba(207, 111, 71, 0.35)',
  '--bs-primary-text-emphasis':'#e3946a',
  '--bs-primary-active':       '#c0603a',
  '--bs-primary-light':        'rgba(207, 111, 71, 0.16)',
  '--bs-primary-inverse':      '#ffffff',
  '--bs-link-color':           '#e3946a',
  '--bs-link-color-rgb':       '227, 148, 106',
  '--bs-link-hover-color':     '#edb08c',
  '--bs-link-hover-color-rgb': '237, 176, 140',

  // Semantic status — brightened one step for dark legibility.
  '--bs-success':              '#5cb567',
  '--bs-success-rgb':          '92, 181, 103',
  '--bs-success-bg-subtle':    'rgba(92, 181, 103, 0.16)',
  '--bs-success-border-subtle':'rgba(92, 181, 103, 0.35)',
  '--bs-danger':               '#e35b46',
  '--bs-danger-rgb':           '227, 91, 70',
  '--bs-danger-bg-subtle':     'rgba(227, 91, 70, 0.16)',
  '--bs-danger-border-subtle': 'rgba(227, 91, 70, 0.35)',
  '--bs-warning':              '#d59a45',
  '--bs-warning-rgb':          '213, 154, 69',
  '--bs-warning-bg-subtle':    'rgba(213, 154, 69, 0.16)',
  '--bs-warning-border-subtle':'rgba(213, 154, 69, 0.35)',
  '--bs-info':                 '#5f97c4',
  '--bs-info-rgb':             '95, 151, 196',
  '--bs-info-bg-subtle':       'rgba(95, 151, 196, 0.16)',
  '--bs-info-border-subtle':   'rgba(95, 151, 196, 0.35)',
  // Status text-emphasis (used by .badge-light-*/.text-*-emphasis on the *-subtle
  // tints) — brightened so they read on the dark tint, not Metronic's dark default.
  '--bs-success-text-emphasis':'#7cc985',
  '--bs-danger-text-emphasis': '#ef8878',
  '--bs-warning-text-emphasis':'#e0b46a',
  '--bs-info-text-emphasis':   '#8bb6d8',

  // Metronic component hover bg/colour (pagination page-link, dropdown/plugin
  // menu :hover, apexcharts export menu) — default light #f4f6fa / blue; flip dark.
  '--bs-component-hover-bg':    'rgba(255, 249, 242, 0.06)',
  '--bs-component-hover-color': '#e3946a',

  // Interactive states. -inverse stays white on the saturated colours; the
  // light/dark families flip (they swap roles under a dark theme).
  '--bs-success-active':       '#4fa259',
  '--bs-success-light':        'rgba(92, 181, 103, 0.16)',
  '--bs-success-inverse':      '#ffffff',
  '--bs-danger-active':        '#cf4c39',
  '--bs-danger-light':         'rgba(227, 91, 70, 0.16)',
  '--bs-danger-inverse':       '#ffffff',
  '--bs-warning-active':       '#c2893a',
  '--bs-warning-light':        'rgba(213, 154, 69, 0.16)',
  '--bs-warning-inverse':      '#ffffff',
  '--bs-info-active':          '#528bb8',
  '--bs-info-light':           'rgba(95, 151, 196, 0.16)',
  '--bs-info-inverse':         '#ffffff',

  '--bs-secondary':            '#837e76',
  '--bs-secondary-rgb':        '131, 126, 118',
  '--bs-secondary-active':     '#948f86',
  '--bs-secondary-light':      'rgba(131, 126, 118, 0.16)',
  '--bs-secondary-inverse':    '#f5f3ef',
  // "light" is now a raised DARK surface; "dark" is now light text.
  '--bs-light':                '#2b2a27',
  '--bs-light-rgb':            '43, 42, 39',
  '--bs-light-active':         '#34322e',
  '--bs-light-light':          '#302f2b',
  '--bs-light-inverse':        '#e9e7e2',
  '--bs-dark':                 '#e9e7e2',
  '--bs-dark-rgb':             '233, 231, 226',
  '--bs-dark-active':          '#f5f3ef',
  '--bs-dark-light':           'rgba(233, 231, 226, 0.12)',
  '--bs-dark-inverse':         '#1a1917',

  // Heading + gray scale (the --bs-* variants, distinct from the --kt-* ones
  // above). Bootstrap/Metronic colour bare <h1>-<h6> via --bs-heading-color /
  // --bs-gray-900, and .card-label / card+modal titles via --bs-text-dark — all
  // of which default to navy #181c32 and are NOT shadowed (they're :root-level),
  // so overriding them here flips every card/modal/section heading under Onyx.
  '--bs-heading-color':        '#e9e7e2',
  '--bs-text-dark':            '#e9e7e2',
  '--bs-gray-900':             '#e9e7e2',
  '--bs-gray-800':             '#d0cdc7',
  '--bs-gray-700':             '#b6b2ab',
  '--bs-gray-600':             '#98938b',
  '--bs-gray-500':             '#837e76',
  '--bs-gray-400':             '#625e58',
  '--bs-gray-300':             '#45433e',
  '--bs-gray-200':             '#34322e',
  '--bs-gray-100':             '#292824',

  // ── Metronic app-level ──────────────────────────────────────────────────
  '--kt-page-bg':                            '#1a1917',
  '--kt-app-bg-color':                       '#1a1917',
  '--kt-app-header-base-bg-color':           'rgba(35, 34, 31, 0.72)',
  '--kt-app-header-sticky-bg-color':         'rgba(35, 34, 31, 0.85)',
  '--kt-app-header-base-border-color':       '#34322e',
  '--kt-app-sidebar-base-bg-color':          'rgba(35, 34, 31, 0.80)',
  '--kt-app-sidebar-base-border-color':      '#34322e',
  '--kt-app-aside-base-bg-color':            'rgba(35, 34, 31, 0.80)',
  '--kt-app-aside-base-border-color':        '#34322e',
  '--kt-app-footer-bg-color':                '#1a1917',
  '--kt-card-bg':              '#232220',
  '--kt-card-border-color':    '#34322e',
  '--kt-card-box-shadow':      '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.45)',

  // Text + gray ramp INVERTED: 900 = brightest text, 100 = a dark surface.
  '--kt-text-dark':            '#f5f3ef',
  '--kt-text-gray-900':        '#e9e7e2',
  '--kt-text-gray-800':        '#d0cdc7',
  '--kt-text-gray-700':        '#b6b2ab',
  '--kt-text-gray-600':        '#98938b',
  '--kt-text-gray-500':        '#837e76',
  '--kt-text-gray-400':        '#625e58',
  '--kt-text-gray-300':        '#45433e',
  '--kt-text-gray-200':        '#34322e',
  '--kt-text-gray-100':        '#292824',
  '--kt-gray-900':             '#e9e7e2',
  '--kt-gray-800':             '#d0cdc7',
  '--kt-gray-700':             '#b6b2ab',
  '--kt-gray-600':             '#98938b',
  '--kt-gray-500':             '#837e76',
  '--kt-gray-400':             '#625e58',
  '--kt-gray-300':             '#45433e',
  '--kt-gray-200':             '#34322e',
  '--kt-gray-100':             '#292824',

  '--kt-primary':              '#cf6f47',
  '--kt-primary-active':       '#c0603a',
  '--kt-primary-light':        'rgba(207, 111, 71, 0.16)',
  '--kt-primary-inverse':      '#ffffff',
  '--kt-border-color':         '#3d3b36',
  '--kt-border-dashed-color':  '#45433e',
  '--kt-input-bg':             '#232220',
  '--kt-input-solid-bg':       '#2b2a27',
  '--kt-input-color':          '#e9e7e2',
  '--kt-input-border-color':   '#3d3b36',
  '--kt-input-placeholder-color':'#837e76',
  '--kt-menu-link-color-hover':  '#e3946a',
  '--kt-menu-link-color-active': '#e3946a',
  '--kt-menu-link-bg-hover':     'rgba(207, 111, 71, 0.10)',
  '--kt-menu-link-bg-active':    'rgba(207, 111, 71, 0.16)',
  '--kt-tooltip-bg':           '#e8e6e2',
  '--kt-tooltip-color':        '#1a1917',
  '--kt-modal-bg':             '#232220',
  '--kt-modal-header-border-color': '#34322e',
  '--kt-modal-footer-border-color': '#34322e',
  '--kt-dropdown-bg':          '#2a2925',
  '--kt-dropdown-border-color':'#3d3b36',
  '--kt-dropdown-link-color':  '#e9e7e2',
  '--kt-dropdown-link-bg-hover':'rgba(207, 111, 71, 0.10)',

  // ── Graphite-specific tokens (the wall + bespoke chrome) ────────────────
  '--graphite-color-scheme': 'dark',
  '--graphite-on-accent':  '#1a1917',
  '--graphite-on-positive': '#1a1917',
  '--graphite-canvas':     '#1a1917',
  '--graphite-surface':    '#232220',
  '--graphite-surface-2':  '#2b2a27',
  '--graphite-glass':      'rgba(35, 34, 31, 0.60)',
  '--graphite-text':       '#e9e7e2',
  '--graphite-text-2':     '#98938b',
  '--graphite-text-3':     '#837e76',
  '--graphite-sep':        'rgba(255, 249, 242, 0.10)',
  '--graphite-accent':     '#cf6f47',
  '--graphite-accent-strong': '#c0603a',
  '--graphite-accent-tint':'rgba(207, 111, 71, 0.16)',
  '--graphite-accent-text':'#e3946a',
  '--graphite-positive':   '#5cb567',
  '--graphite-alert':      '#e35b46',
  '--graphite-warn':       '#d59a45',
  '--graphite-shadow-sm':  '0 1px 2px rgba(0, 0, 0, 0.45)',
  '--graphite-shadow':     '0 1px 2px rgba(0, 0, 0, 0.4), 0 8px 24px rgba(0, 0, 0, 0.45)',
  '--graphite-shadow-lg':  '0 16px 44px rgba(0, 0, 0, 0.60)',

  // Dark-only tokens for the SCSS values that were hardcoded light (Graphite +
  // Blossom leave these unset → SCSS fallback = the old light value).
  '--graphite-chrome-glass': 'rgba(35, 34, 31, 0.72)',
  '--graphite-chrome-aside': 'rgba(35, 34, 31, 0.82)',
  '--graphite-scroll-thumb': 'rgba(255, 249, 242, 0.16)',
  '--graphite-scroll-thumb-hover': 'rgba(255, 249, 242, 0.26)',
  '--graphite-hover':        'rgba(255, 249, 242, 0.06)',
  '--graphite-warn-text':    '#e0a94e',
  // Bootstrap's .btn-close glyph is a baked BLACK svg (invisible on dark modals);
  // graphite pins data-bs-theme=light so its dark auto-invert never fires. This
  // filter (set ONLY on Onyx; Graphite/Blossom leave it unset → 'none') inverts
  // it to a light glyph. Applied via a .btn-close rule in global.scss.
  '--graphite-btn-close-filter': 'invert(1) grayscale(1) brightness(1.85)',
};

export const ONYX_THEME: BuiltInTheme = {
  id: '__builtin_onyx',
  slug: 'onyx',
  name: 'Onyx',
  description: 'Graphite after dark — a warm-charcoal night palette with the terracotta accent brightened to glow. Easy on the eyes at night.',
  isBuiltIn: true,
  appThemeAttr: 'graphite', // reuse graphite's chrome + scoped SCSS; only the palette differs
  swatch: 'linear-gradient(135deg, #232220 0 58%, #cf6f47 58% 100%)', // charcoal + terracotta corner
  overrides: {
    light: ONYX_OVERRIDES,
    dark:  ONYX_OVERRIDES,
  },
};
